/**
 * TipTap extension that keeps a highlight over each text range the user has
 * handed to a chat composer as a context chip.
 *
 * The "Select" action that creates a chip has to collapse the real ProseMirror
 * selection (otherwise the bubble menu offering it never dismisses) and then
 * moves focus to the composer — and no browser paints the selection of an
 * unfocused contenteditable. So the range the chip refers to needs a highlight
 * of its own to stay visible while the user types their instruction.
 *
 * The highlight is a DECORATION, deliberately, never a `Highlight` mark: a mark
 * mutates the document, which fires the editor's `onUpdate` and gets
 * debounce-saved into the artifact the document came from. Decorations are
 * invisible to `getMarkdown()`.
 *
 * Each highlight is owned by the chip whose id it carries, and the caller owns
 * the list: pass every current chip's anchor to `setSelectionHighlights`, and a
 * chip the user removed (or one cleared by sending the message) simply stops
 * being in the list.
 *
 * @module SelectionHighlightExtension
 */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

import {
  createTextAnchorResolver,
  type ISelectionTextAnchor,
} from './selection-text-range';

/** Class the decoration paints with; styled in the automation module's CSS. */
export const SELECTION_HIGHLIGHT_CLASS = 'selection-context-highlight';

/** A highlighted range, owned by the context chip whose id it carries. */
export interface ISelectionHighlightAnchor extends ISelectionTextAnchor {
  /** Chip id; the highlight lives and dies with that chip. */
  id: string;
}

interface ISelectionHighlightState {
  /** Anchors the caller last set, in chip order. */
  anchors: ISelectionHighlightAnchor[];
  /** Decorations currently painted for those anchors. */
  decorations: DecorationSet;
}

/** Decoration spec, so a mapped decoration still knows its chip. */
interface ISelectionHighlightSpec {
  id: string;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    selectionHighlight: {
      /**
       * Highlights exactly these ranges, replacing whatever was highlighted
       * before. An empty array clears them.
       */
      setSelectionHighlights: (
        anchors: ISelectionHighlightAnchor[],
      ) => ReturnType;
      /** Clears every highlight. */
      clearSelectionHighlights: () => ReturnType;
    };
  }
}

export const selectionHighlightPluginKey =
  new PluginKey<ISelectionHighlightState>('selectionHighlight');

/**
 * Builds one decoration per anchor that still resolves against `doc`. An anchor
 * whose text is no longer in the document contributes nothing — the chip stays
 * (the agent is still getting its text), it just has no highlight to show.
 *
 * All the anchors share one index of the document, so painting a set of chips
 * costs a single walk of a blueprint that can be long rather than one per chip.
 *
 * @param {ProseMirrorNode} doc - Document to resolve the anchors against
 * @param {ISelectionHighlightAnchor[]} anchors - Anchors to paint
 * @returns {Decoration[]} Resolved decorations
 */
function buildDecorations(
  doc: ProseMirrorNode,
  anchors: ISelectionHighlightAnchor[],
): Decoration[] {
  if (anchors.length === 0) return [];
  const resolve = createTextAnchorResolver(doc);

  return anchors.flatMap(anchor => {
    const range = resolve(anchor);
    if (!range) return [];
    const spec: ISelectionHighlightSpec = { id: anchor.id };
    return [
      Decoration.inline(
        range.from,
        range.to,
        { class: SELECTION_HIGHLIGHT_CLASS },
        spec,
      ),
    ];
  });
}

/**
 * True when the transaction threw the whole document away and put another one
 * in its place, which is what `setContent` does — a save round-trip echoing the
 * server's content, or a committed agent edit. Every position from the old
 * document is then meaningless and the anchors have to be re-found by text.
 *
 * Detected from the steps rather than from the decorations, because "all the
 * decorations mapped away" cannot tell a replaced document apart from a user
 * deleting the highlighted text — and re-finding by text in the second case
 * moves the highlight to some other copy of the same words.
 *
 * @param {Transaction} tr - Transaction to inspect
 * @returns {boolean} True if some step replaced the document's entire content
 */
function replacesEntireDoc(tr: Transaction): boolean {
  return tr.steps.some((step, index) => {
    const docBefore = tr.docs[index];
    if (!docBefore) return false;
    let coversDoc = false;
    step.getMap().forEach((oldStart, oldEnd) => {
      if (oldStart <= 0 && oldEnd >= docBefore.content.size) coversDoc = true;
    });
    return coversDoc;
  });
}

/**
 * Sets the plugin's metadata on a transaction and dispatches it.
 *
 * The transaction changes no content, so TipTap emits no `update` event and
 * nothing downstream debounce-saves the document; `addToHistory: false` keeps
 * highlight churn out of the undo stack as well.
 *
 * @param {ISelectionHighlightAnchor[]} anchors - Anchors to set
 * @param {Transaction} tr - The command's transaction
 * @param {Function} [dispatch] - Omitted when the command is only being probed
 * @returns {boolean} Always true (the command never fails)
 */
function dispatchAnchors(
  anchors: ISelectionHighlightAnchor[],
  tr: Transaction,
  dispatch?: (tr: Transaction) => void,
): boolean {
  if (dispatch) {
    tr.setMeta(selectionHighlightPluginKey, { anchors });
    tr.setMeta('addToHistory', false);
    dispatch(tr);
  }
  return true;
}

export const SelectionHighlightExtension = Extension.create({
  name: 'selectionHighlight',

  addCommands() {
    return {
      setSelectionHighlights:
        (anchors: ISelectionHighlightAnchor[]) =>
        ({
          tr,
          dispatch,
        }: {
          tr: Transaction;
          dispatch?: (tr: Transaction) => void;
        }) =>
          dispatchAnchors(anchors, tr, dispatch),

      clearSelectionHighlights:
        () =>
        ({
          tr,
          dispatch,
        }: {
          tr: Transaction;
          dispatch?: (tr: Transaction) => void;
        }) =>
          dispatchAnchors([], tr, dispatch),
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<ISelectionHighlightState>({
        key: selectionHighlightPluginKey,

        state: {
          init: () => ({ anchors: [], decorations: DecorationSet.empty }),

          apply: (tr, value) => {
            const meta = tr.getMeta(selectionHighlightPluginKey) as
              | { anchors?: ISelectionHighlightAnchor[] }
              | undefined;
            if (meta?.anchors) {
              return {
                anchors: meta.anchors,
                decorations: DecorationSet.create(
                  tr.doc,
                  buildDecorations(tr.doc, meta.anchors),
                ),
              };
            }

            if (!tr.docChanged || value.anchors.length === 0) return value;

            // A replaced document invalidates every position, so re-find the
            // anchors by text against the new one. An anchor whose text the
            // replacement does not contain resolves to nothing and stays
            // unhighlighted; the chip itself stays either way.
            if (replacesEntireDoc(tr)) {
              return {
                anchors: value.anchors,
                decorations: DecorationSet.create(
                  tr.doc,
                  buildDecorations(tr.doc, value.anchors),
                ),
              };
            }

            // Every other edit is relative to the document the highlights were
            // resolved against, so map the live ranges through it rather than
            // re-finding them. Mapping follows an edit made *inside* a
            // highlighted range, which a text search could not, and it drops a
            // range the user deleted instead of re-finding it — blueprints
            // repeat themselves, and a re-find here would move the highlight
            // onto another copy of the same words that the user never selected.
            return {
              anchors: value.anchors,
              decorations: value.decorations.map(tr.mapping, tr.doc),
            };
          },
        },

        props: {
          decorations: state =>
            selectionHighlightPluginKey.getState(state)?.decorations ??
            DecorationSet.empty,
        },
      }),
    ];
  },
});
